import { DateTime } from 'luxon'
import { isNonEmptyNumber } from '@labrav/common'
import { isNull, isUndefined } from 'lodash'
import { Installments } from '../../../types/flyout'

/** Figma renewal invoice dates: "Jan 05, 2026" */
const FIGMA_INVOICE_DATE_FORMAT = 'LLL dd, yyyy'

export type InstallmentsViewLayout = 'invoiceDate' | 'renewalSchedule' | 'auto'

export const getCalculatedTcv = (installments: Installments[]) => {
  return (installments || []).reduce((acc, curr) => {
    const amount = curr.amount
    if (!isUndefined(amount) && !isNull(amount)) {
      return acc + Number(amount)
    }
    return acc
  }, 0)
}

export const getUpliftedTcv = (tcv: number, upliftPercent: number) =>
  Math.round(tcv * (1 + (Number(upliftPercent) || 0) / 100) * 100) / 100

/** Details tables show $. API/form rows often only have % of TCV. */
export const withInstallmentAmounts = (
  rows: Installments[] | undefined,
  tcv: number
): Installments[] =>
  (rows || []).map(row => ({
    ...row,
    amount: isNonEmptyNumber(row.amount as number)
      ? row.amount
      : row.percentage != null &&
          isNonEmptyNumber(Number(row.percentage)) &&
          tcv > 0
        ? Math.round((Number(row.percentage) / 100) * tcv * 100) / 100
        : row.amount,
  }))

export const resolvePercent = (installment: Installments, tcv: number) => {
  if (
    installment.percentage != null &&
    isNonEmptyNumber(installment.percentage)
  ) {
    return Number(installment.percentage)
  }
  const amount = installment.amount
  if (tcv > 0 && isNonEmptyNumber(amount as number)) {
    return Math.round((Number(amount) / tcv) * 100)
  }
  return null
}

export const shouldUseRenewalScheduleLayout = (
  layout: InstallmentsViewLayout,
  installments?: Installments[]
) => {
  if (layout === 'renewalSchedule') return true
  if (layout === 'invoiceDate') return false
  const rows = installments || []
  const hasMonthsAfter = rows.some(row => row.monthsAfter != null)
  const hasPaymentDate = rows.some(row => !!row.paymentDate)
  return hasMonthsAfter && !hasPaymentDate
}

export const formatInvoiceDate = (
  iso: string | undefined,
  timeZone: string
) => {
  if (!iso) return '-'
  const dt = DateTime.fromISO(iso, { zone: timeZone })
  return dt.isValid ? dt.toFormat(FIGMA_INVOICE_DATE_FORMAT) : '-'
}

export const resolveInvoiceDateIso = (
  row: Installments,
  contractStartDate?: string
) => {
  if (row.paymentDate) return row.paymentDate
  if (!contractStartDate || row.monthsAfter == null) return undefined
  const start = DateTime.fromISO(contractStartDate)
  if (!start.isValid) return undefined
  const shifted = start.plus({ months: Number(row.monthsAfter) })
  const requested = Number(row.dayOfMonth ?? start.day)
  const day = Math.min(Math.max(1, requested || 1), shifted.daysInMonth)
  return shifted.set({ day }).toISODate() ?? undefined
}
